import React, { useEffect, useState, useRef } from 'react';
import { Navbar, Nav, Badge, Form } from 'react-bootstrap';
import { IoIosBasket } from 'react-icons/io';
import { FiUser } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Header.css';
import logo from '../Images/vegboxlogo.svg';

import { useCart } from './CartContext'; // Import the CartContext
import BASE_URL from '../../config';


const HeaderDe = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const navigate = useNavigate();
  const authToken = localStorage.getItem('authToken');
  const { cart, loading, error } = useCart();

  const [language] = useState(localStorage.getItem('language') || 'en');
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const langMobileRef = useRef(null);
  const accountRef = useRef(null);
  const accountMobileRef = useRef(null);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inDesktop = langRef.current && langRef.current.contains(e.target);
      const inMobile = langMobileRef.current && langMobileRef.current.contains(e.target);
      if (!inDesktop && !inMobile) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const switchLanguage = (lang) => {
    localStorage.setItem('language', lang);
    window.location.reload();
  };


  // Check if the user is logged in
  useEffect(() => {
    setIsLoggedIn(!!authToken);
  }, [authToken]);

  // Close account dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        accountRef.current && !accountRef.current.contains(e.target) &&
        accountMobileRef.current && !accountMobileRef.current.contains(e.target)
      ) {
        setAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Logout
  const handleLogout = async () => {
    try {
      await axios.post(
        `${BASE_URL}/api/logout/`,
        {},
        { headers: { Authorization: `Token ${authToken}` } }
      );
      localStorage.removeItem('authToken');
      setIsLoggedIn(false);
      navigate('/');
    } catch (err) {
      console.error('Error during logout:', err);
    }
  };

  // Cart logic - optional if needed
  const totalItems = cart?.items?.reduce((acc, item) => acc + item.quantity, 0) || 0;

  // Handle search input change
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.length > 1) {
        axios
          .get(`${BASE_URL}/api/products/?format=json`)
          .then((res) => {
            const filtered = res.data.filter((product) =>
              product.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setSuggestions(filtered);
            setShowSuggestions(true);
          })
          .catch((error) => {
            console.error('Error fetching suggestions:', error);
          });
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300); // debounce 300ms

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleSelect = (slug) => {
    navigate(`/product/${slug}`);
    setSearchTerm('');
    setShowSuggestions(false);
  };

  return (
    <div className='container-fluid bgprimary'>
      <Navbar bg="none" expand="lg" className="py-2 container">
        <Navbar.Brand href="/"><img src={logo} alt="logo" /></Navbar.Brand>
        <div className='header-mobile-actions d-none'>
          <div className="account-switcher" ref={accountMobileRef}>
            <button className="account-btn" onClick={() => setAccountOpen(o => !o)}>
              <FiUser className="account-icon" />
            </button>
            {accountOpen && (
              <div className="account-dropdown">
                {isLoggedIn ? (
                  <>
                    <a href="/history" className="account-option">Meine Bestellungen</a>
                    <button className="account-option" onClick={handleLogout}>Ausloggen</button>
                  </>
                ) : (
                  <>
                    <a href="/login" className="account-option">Anmelden</a>
                    <a href="/register" className="account-option">Konto erstellen</a>
                  </>
                )}
              </div>
            )}
          </div>
          <a href='/cart' className='header-cart-icon'>
            <IoIosBasket style={{ color: 'black' }} />
            {totalItems > 0 && <Badge pill bg="success" className="header-cart-badge">{totalItems}</Badge>}
          </a>
        </div>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />

        <Form className="position-relative d-flex ml-3 specialform header-search" autoComplete="off">
          <Form.Control
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Artikel suchen"
            className="mr-2"
            aria-label="Search"
          />
          {showSuggestions && suggestions.length > 0 && (
            <div className="search-suggestions position-absolute bg-white border rounded shadow p-2 w-100" style={{ top: '100%', zIndex: 1000 }}>
              {suggestions.map((product) => (
                <div
                  key={product.id}
                  className="d-flex align-items-center mb-2 search-suggestion-item"
                  onClick={() => handleSelect(product.slug)}
                  style={{ cursor: 'pointer' }}
                >
                  <img src={product.mainimage} alt={product.title} width="50" height="50" className="me-2" />
                  <div>
                    <div className="fw-bold">{product.title}</div>
                    <div>CHF {product.discount_price ?? product.price}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Form>

        <Navbar.Collapse id="basic-navbar-nav">

          <Nav className="me-auto mobile-nav-links">
            <Nav.Link href="/products">Produkte</Nav.Link>
            <Nav.Link href="/categories">Kategorien</Nav.Link>
            <Nav.Link href="/about-us">Über uns</Nav.Link>
            <Nav.Link href="/contact">Kontakt</Nav.Link>
          </Nav>

          {/* Language switcher inside hamburger (mobile only) */}
          <div className="lang-switcher d-lg-none" ref={langMobileRef}>
            <button className="lang-active-btn" onClick={() => setLangOpen(o => !o)}>
              <img
                src={language === 'en' ? 'https://flagcdn.com/w40/gb.png' : 'https://flagcdn.com/w40/de.png'}
                alt={language.toUpperCase()}
                className="lang-flag"
              />
              {language.toUpperCase()}
              <span className="lang-chevron">{langOpen ? '▲' : '▼'}</span>
            </button>
            {langOpen && (
              <div className="lang-dropdown">
                <button
                  className="lang-option"
                  onClick={() => switchLanguage(language === 'en' ? 'de' : 'en')}
                >
                  <img
                    src={language === 'en' ? 'https://flagcdn.com/w40/de.png' : 'https://flagcdn.com/w40/gb.png'}
                    alt={language === 'en' ? 'DE' : 'EN'}
                    className="lang-flag"
                  />
                  {language === 'en' ? 'DE' : 'EN'}
                </button>
              </div>
            )}
          </div>

        </Navbar.Collapse>

        {/* Desktop right-side controls: lang + account + cart */}
        <div className="header-desktop-right d-none d-lg-flex">
          <div className="lang-switcher" ref={langRef}>
            <button className="lang-active-btn" onClick={() => setLangOpen(o => !o)}>
              <img
                src={language === 'en' ? 'https://flagcdn.com/w40/gb.png' : 'https://flagcdn.com/w40/de.png'}
                alt={language.toUpperCase()}
                className="lang-flag"
              />
              {language.toUpperCase()}
              <span className="lang-chevron">{langOpen ? '▲' : '▼'}</span>
            </button>
            {langOpen && (
              <div className="lang-dropdown">
                <button
                  className="lang-option"
                  onClick={() => switchLanguage(language === 'en' ? 'de' : 'en')}
                >
                  <img
                    src={language === 'en' ? 'https://flagcdn.com/w40/de.png' : 'https://flagcdn.com/w40/gb.png'}
                    alt={language === 'en' ? 'DE' : 'EN'}
                    className="lang-flag"
                  />
                  {language === 'en' ? 'DE' : 'EN'}
                </button>
              </div>
            )}
          </div>

          <div className="account-switcher" ref={accountRef}>
            <button className="account-btn" onClick={() => setAccountOpen(o => !o)}>
              <FiUser className="account-icon" />
              <span className="account-label">Mein Konto</span>
              <span className="lang-chevron">{accountOpen ? '▲' : '▼'}</span>
            </button>
            {accountOpen && (
              <div className="account-dropdown">
                {isLoggedIn ? (
                  <>
                    <a href="/history" className="account-option">Meine Bestellungen</a>
                    <button className="account-option" onClick={handleLogout}>Ausloggen</button>
                  </>
                ) : (
                  <>
                    <a href="/login" className="account-option">Anmelden</a>
                    <a href="/register" className="account-option">Konto erstellen</a>
                  </>
                )}
              </div>
            )}
          </div>

          <a href='/cart' className='header-cart-icon'>
            <IoIosBasket />
            {totalItems > 0 && <Badge pill bg="success" className="header-cart-badge">{totalItems}</Badge>}
          </a>
        </div>
      </Navbar>
    </div>
  );
};

export default HeaderDe;
